import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import './Shop.css';

const CATEGORIES = [
  { key: 'all',      label: '🛍️ Tous' },
  { key: 'cartes',   label: '🎴 Cartes à collectionner' },
  { key: 'peche',    label: '🎣 Pêche' },
  { key: 'nourriture', label: '🍞 Nourritures/Boissons' },
  { key: 'autres',   label: '📦 Autres' },
];

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

export default function Shop() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]); // [{ product, quantity }]
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null); // { success, error, order }
  const [cartError, setCartError] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [boxesLoading, setBoxesLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/shop/products', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProducts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const fetchBoxes = useCallback(() => {
    setBoxesLoading(true);
    axios.get('/api/shop/boxes', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setBoxes(res.data);
        // Pré-sélectionner la première box disponible
        const firstAvailable = res.data.find(b => b.available);
        if (firstAvailable) setSelectedBoxId(firstAvailable.id);
      })
      .catch(() => {})
      .finally(() => setBoxesLoading(false));
  }, [token]);

  // ── Cart helpers ──
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) return prev.map(i => i.product.id === product.id ? { ...i, quantity: Math.min(i.quantity + 1, 10) } : i);
      return [...prev, { product, quantity: 1 }];
    });
    setCheckoutResult(null);
    setCartError(null);
    // Charger les boxes dès qu'on ajoute au panier
    if (boxes.length === 0) fetchBoxes();
  };
  const setQty = (productId, qty) => {
    if (qty <= 0) return removeFromCart(productId);
    setCart(prev => prev.map(i => i.product.id === productId ? { ...i, quantity: Math.min(qty, 10) } : i));
  };
  const removeFromCart = (productId) => setCart(prev => prev.filter(i => i.product.id !== productId));
  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const balanceOk = !user || user.balance >= cartTotal;

  // ── Checkout ──
  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (!balanceOk) { setCartError('Solde insuffisant'); return; }
    if (!selectedBoxId) { setCartError('Veuillez choisir une FastBox'); return; }
    setCheckoutLoading(true);
    setCartError(null);
    try {
      const res = await axios.post('/api/shop/checkout', {
        cart: cart.map(i => ({ productId: i.product.id, quantity: i.quantity })),
        boxId: selectedBoxId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCheckoutResult({ success: true, order: res.data });
      clearCart();
    } catch (e) {
      setCartError(e.response?.data?.error || 'Erreur lors de la commande');
      setCheckoutResult({ success: false, error: e.response?.data?.error || 'Erreur' });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const filtered = category === 'all' ? products : products.filter(p => p.category === category);

  return (
    <AppLayout>
      <div className="page-header">
        <div>
          <h1 className="page-title">Boutique</h1>
          <p className="page-subtitle">Commandez et récupérez en FastBox dans tout Los Santos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="shop-filters">
        {CATEGORIES.map(c => (
          <button key={c.key} className={`filter-btn${category === c.key ? ' active' : ''}`}
            onClick={() => setCategory(c.key)}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="shop-layout">
        {/* ── Products ── */}
        <div>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted)', padding: '40px 0' }}>
              <i className="fa-solid fa-spinner spinner" style={{ color: 'var(--primary)', fontSize: 20 }} />
              Chargement des produits...
            </div>
          ) : (
            <div className="products-grid">
              {filtered.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    <div style={{
                      display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center',
                      flexWrap: 'wrap', minHeight: 100, padding: '8px 0'
                    }}>
                      {product.items?.map((item, idx) => (
                        <div key={idx} style={{ textAlign: 'center' }}>
                          <img
                            src={item.metadata?.image ? `/ox-images/${item.metadata.image}.png` : product.image}
                            alt={item.name}
                            style={{ width: product.items.length > 1 ? 80 : 110, height: product.items.length > 1 ? 80 : 110, objectFit: 'contain', borderRadius: 8 }}
                          />
                          {product.items.length > 1 && (
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                              {item.name} ×{item.count}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <span className="product-category-badge">{product.category}</span>
                  </div>
                  <div className="product-body">
                    <div className="product-name">{product.name}</div>
                    <div className="product-desc">{product.desc}</div>
                  </div>
                  <div className="product-footer">
                    <span className="product-price">{formatAmount(product.price)}</span>
                    <button className="product-add-btn" onClick={() => addToCart(product)}
                      title="Ajouter au panier">
                      <i className="fa-solid fa-plus" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Cart ── */}
        <div className="card cart-card">
          <div className="cart-title">
            <i className="fa-solid fa-cart-shopping" style={{ color: 'var(--primary)' }} />
            Mon panier
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </div>

          {/* Success result */}
          {checkoutResult?.success && (
            <div className="checkout-result checkout-success">
              <div className="checkout-success-icon">
                {checkoutResult.order.status === 'pending'
                  ? <i className="fa-solid fa-hourglass-half" style={{ color: 'var(--primary)' }} />
                  : <i className="fa-solid fa-circle-check" />
                }
              </div>
              {checkoutResult.order.status === 'pending' ? (
                <>
                  <h3>Commande en préparation !</h3>
                  <p>Votre colis est en cours de préparation. Vous recevrez un <strong>DM Discord</strong> dès qu'il sera prêt à récupérer.</p>
                  <div className="delivery-info">
                    <div className="delivery-row">
                      <span className="delivery-label"><i className="fa-solid fa-money-bill" /> Total débité</span>
                      <span className="delivery-value">{formatAmount(checkoutResult.order.total)}</span>
                    </div>
                  </div>
                  {checkoutResult.order.deliverAt && (
                    <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--surface-2, rgba(220,38,38,0.08))', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--primary)', fontWeight: 600, textAlign: 'center' }}>
                      <i className="fa-solid fa-clock" /> Livraison prévue dans {Math.ceil(checkoutResult.order.delayMs / 60000)} min
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3>Commande confirmée !</h3>
                  <p>Votre colis est prêt à être récupéré.</p>
                  <div className="delivery-info">
                    <div className="delivery-row">
                      <span className="delivery-label"><i className="fa-solid fa-location-dot" /> FastBox</span>
                      <span className="delivery-value">{checkoutResult.order.boxStreet}</span>
                    </div>
                    <div className="delivery-row">
                      <span className="delivery-label"><i className="fa-solid fa-key" /> Code</span>
                      <span className="delivery-code">{checkoutResult.order.code}</span>
                    </div>
                    <div className="delivery-row">
                      <span className="delivery-label"><i className="fa-solid fa-money-bill" /> Total</span>
                      <span className="delivery-value">{formatAmount(checkoutResult.order.total)}</span>
                    </div>
                  </div>
                </>
              )}
              <button className="btn-secondary" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
                onClick={() => { setCheckoutResult(null); navigate('/orders'); }}>
                <i className="fa-solid fa-box-open" /> Voir mes commandes
              </button>
            </div>
          )}

          {!checkoutResult?.success && (
            <>
              {cart.length === 0 ? (
                <div className="cart-empty">
                  <div className="cart-empty-icon">🛒</div>
                  <p>Votre panier est vide.</p>
                  <p style={{ marginTop: 4, fontSize: 12 }}>Ajoutez des produits depuis le catalogue.</p>
                </div>
              ) : (
                <>
                  <div className="cart-items">
                    {cart.map(item => (
                      <div key={item.product.id} className="cart-item">
                        <img src={item.product.image} alt={item.product.name} className="cart-item-emoji" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6 }} />
                        <div className="cart-item-info">
                          <div className="cart-item-name">{item.product.name}</div>
                          <div className="cart-item-price">{formatAmount(item.product.price)} × {item.quantity} = {formatAmount(item.product.price * item.quantity)}</div>
                        </div>
                        <div className="cart-item-qty">
                          <button className="qty-btn" onClick={() => setQty(item.product.id, item.quantity - 1)}>
                            <i className="fa-solid fa-minus" />
                          </button>
                          <span className="qty-num">{item.quantity}</span>
                          <button className="qty-btn" onClick={() => setQty(item.product.id, item.quantity + 1)}>
                            <i className="fa-solid fa-plus" />
                          </button>
                        </div>
                        <button className="cart-remove-btn" onClick={() => removeFromCart(item.product.id)}>
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <hr className="cart-divider" />

                  <div className="cart-total-row">
                    <span className="cart-total-label">Sous-total</span>
                    <span className="cart-total-value">{formatAmount(cartTotal)}</span>
                  </div>
                  <div className="cart-total-row">
                    <span className="cart-total-label" style={{ fontWeight: 700 }}>Total</span>
                    <span className="cart-total-value cart-total-final">{formatAmount(cartTotal)}</span>
                  </div>

                  {/* Sélecteur de FastBox */}
                  <hr className="cart-divider" />
                  <div className="box-picker">
                    <div className="box-picker-title">
                      <i className="fa-solid fa-map-pin" style={{ color: 'var(--primary)' }} /> Choisir votre FastBox
                      <button className="box-picker-refresh" onClick={fetchBoxes} title="Actualiser" disabled={boxesLoading}>
                        <i className={`fa-solid fa-rotate-right${boxesLoading ? ' spinner' : ''}`} />
                      </button>
                    </div>
                    {boxesLoading ? (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
                        <i className="fa-solid fa-spinner spinner" /> Chargement...
                      </div>
                    ) : (
                      <div className="box-picker-list">
                        {boxes.map(box => (
                          <button
                            key={box.id}
                            className={`box-picker-item${selectedBoxId === box.id ? ' selected' : ''}`}
                            onClick={() => setSelectedBoxId(box.id)}
                          >
                            <i className="fa-solid fa-box" />
                            <span className="box-picker-street">{box.streetName}</span>
                            {selectedBoxId === box.id && <i className="fa-solid fa-check box-picker-check" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {cartError && (
                    <div style={{ padding: '10px 12px', background: 'var(--error-bg)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--error)', marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <i className="fa-solid fa-triangle-exclamation" />
                      {cartError}
                    </div>
                  )}

                  <button
                    className="btn-primary cart-checkout-btn"
                    onClick={handleCheckout}
                    disabled={checkoutLoading || !balanceOk || cart.length === 0 || !selectedBoxId}
                  >
                    {checkoutLoading
                      ? <><i className="fa-solid fa-spinner spinner" /> Traitement...</>
                      : <><i className="fa-solid fa-credit-card" /> Payer {formatAmount(cartTotal)}</>
                    }
                  </button>

                  <div className="cart-balance-info">
                    {user && (
                      balanceOk
                        ? <span>Solde disponible : <strong style={{ color: 'var(--success)' }}>{formatAmount(user.balance)}</strong></span>
                        : <span className="insufficient"><i className="fa-solid fa-triangle-exclamation" /> Solde insuffisant — {formatAmount(user.balance)} disponible</span>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
